import axios from '../../services/api-axios';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setParitipants, setTeam, setHost, setReadyPlayers, setBots, setSessionTarget, setLobbyName, disconnectFromLobby, removeLobbyFromList } from '../../store/lobbySlice';
import { unsubscribe } from '../../services/ws-service';
import { isBotName } from '../../utils/botName';
// @ts-ignore - plain JSX component
import PlayerAvatar from '../common/PlayerAvatar';
import FinishedGameDetailsModal from '../Game/FinishedGameDetailsModal';
import styles from './LobbyRoom.module.css';

type Team = 'TEAM1' | 'TEAM2' | 'UNDEFINED';
type Difficulty = 'EASY' | 'MEDIUM' | 'HARD';

interface FinishedGameShort {
    gameId: string;
    team1Player1: string;
    team1Player2: string;
    team2Player1: string;
    team2Player2: string;
    team1Score: number;
    team2Score: number;
}

interface LobbyRoomProps {
    lobbyId: string;
}

const LobbyRoom = ({ lobbyId }: LobbyRoomProps) => {
    const [games, setGames] = useState<FinishedGameShort[]>([]);
    const [myTeam, setMyTeam] = useState<Team>('UNDEFINED');
    const [newBotDifficulty, setNewBotDifficulty] = useState<Record<string, Difficulty>>({
        TEAM1: 'EASY',
        TEAM2: 'EASY'
    });
    const participants = useAppSelector((state) => state.lobby.participants);
    const team1 = useAppSelector((state) => state.lobby.team1);
    const team2 = useAppSelector((state) => state.lobby.team2);
    const host = useAppSelector((state) => state.lobby.host);
    const readyPlayers = useAppSelector((state) => state.lobby.readyPlayers);
    const bots = useAppSelector((state) => state.lobby.bots);
    const sessionTarget = useAppSelector((state) => state.lobby.sessionTarget);
    const myUsername = useAppSelector((state) => state.onlineUsers.username);

    const dispatch = useAppDispatch();
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [inviteCode, setInviteCode] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const isHost = myUsername === host;
    const amIReady = readyPlayers.includes(myUsername);

    const unassignedUsers = participants.filter(user =>
        !team1.includes(user) && !team2.includes(user)
    );

    useEffect(() => {
        const loadLobby = async () => {
            try {
                const res = await axios.get(`/rest/lobby/info/${lobbyId}`);
                dispatch(setParitipants(res.data.participants));
                dispatch(setTeam({ team: "TEAM1", users: res.data.team1 }));
                dispatch(setTeam({ team: "TEAM2", users: res.data.team2 }));
                dispatch(setHost(res.data.host));
                dispatch(setReadyPlayers(res.data.readyPlayers || []));
                dispatch(setBots(res.data.botDifficulties || {}));
                dispatch(setSessionTarget(res.data.sessionTarget || undefined));
                setGames(res.data.finishedGamesShort || []);
                setInviteCode(res.data.inviteCode || null);
            dispatch(setLobbyName(res.data.name || undefined));
                return true;
            } catch (err) {
                console.error(err);
                return false;
            }
        };

        const openLobby = async () => {
            if (await loadLobby()) return;
            // The endpoint answers 500 both for a lobby that is gone and for a server
            // hiccup, so confirm before acting on it.
            await new Promise(resolve => setTimeout(resolve, 800));
            if (await loadLobby()) return;

            // A listed lobby whose state cannot be read no longer exists: a session
            // died without releasing it. Drop back to the list rather than sitting in
            // a room that will never have anybody in it.
            unsubscribe(`/topic/public/lobby/${lobbyId}`);
            dispatch(removeLobbyFromList({ lobbyId }));
            dispatch(disconnectFromLobby());
        };

        openLobby();

        return () => {
            dispatch(setParitipants([]));
            dispatch(setTeam({ team: "TEAM1", users: [] }));
            dispatch(setTeam({ team: "TEAM2", users: [] }));
        };
    }, []);

    const joinTeam = (team: 'TEAM1' | 'TEAM2') => {
        const targetTeam = team === "TEAM1" ? team1 : team2;
        if (targetTeam.length >= 2) {
            alert(`${team === "TEAM1" ? "Team 1" : "Team 2"} is full! Maximum 2 players per team.`);
            return;
        }

        if (myTeam !== team) {
            axios.get(`/rest/lobby/${lobbyId}/${team}`).then(() => {
                setMyTeam(team);
            }).catch(err => {
                console.error(err);
            });
        }
    };

    const leaveTeam = () => {
        if (myTeam !== "UNDEFINED") {
            axios.get(`/rest/lobby/${lobbyId}/UNDEFINED`).then(() => {
                setMyTeam("UNDEFINED");
            }).catch(err => {
                console.error(err);
            });
        }
    };

    const assignPlayerToTeam = (username: string, team: Team) => {
        axios.put(`/rest/lobby/${lobbyId}/assign`, { username, team }).catch(err => {
            console.error(err);
        });
    };

    const toggleReady = () => {
        axios.post(`/rest/lobby/${lobbyId}/ready`).catch(err => {
            console.error(err);
        });
    };

    const addBotToTeam = (team: 'TEAM1' | 'TEAM2') => {
        axios.post(`/rest/lobby/${lobbyId}/bot`, { team, difficulty: newBotDifficulty[team] }).catch(err => {
            console.error(err);
        });
    };

    const removeBotFromLobby = (botName: string) => {
        axios.delete(`/rest/lobby/${lobbyId}/bot/${encodeURIComponent(botName)}`).catch(err => {
            console.error(err);
        });
    };

    const makeHost = (username: string) => {
        axios.put(`/rest/lobby/${lobbyId}/host`, { username }).catch(err => {
            console.error(err);
        });
    };

    const startGame = () => {
        axios.post('/rest/game', { lobbyId }).catch(error => {
            console.error(error);
        });
    };

    const inviteLink = inviteCode ? `${window.location.origin}/join/${inviteCode}` : null;

    const copyInviteLink = async () => {
        if (!inviteLink) return;
        try {
            // navigator.clipboard needs a secure context; fall back for plain-HTTP dev hosts.
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(inviteLink);
            } else {
                const field = document.createElement('textarea');
                field.value = inviteLink;
                field.style.position = 'fixed';
                field.style.opacity = '0';
                document.body.appendChild(field);
                field.select();
                document.execCommand('copy');
                document.body.removeChild(field);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard denied: show the raw link so it can still be copied by hand.
            window.prompt('Copy this invite link:', inviteLink);
        }
    };

    const handleDetailsClick = (gameId: string) => {
        setSelectedId(gameId);
    };

    const renderNameBadges = (user: string) => (
        <>
            {user === host && <span className={styles.hostBadge} title="Host">👑</span>}
            {isBotName(user) && (
                <span className={styles.botDifficultyLabel}>🤖 {bots[user] || 'EASY'}</span>
            )}
            {!isBotName(user) && user !== host && (
                <span className={`${styles.readyBadge} ${readyPlayers.includes(user) ? styles.readyBadgeReady : styles.readyBadgeNotReady}`}>
                    {readyPlayers.includes(user) ? 'Ready' : 'Not Ready'}
                </span>
            )}
        </>
    );

    const renderHostControls = (user: string, currentTeam: Team) => {
        if (!isHost || isBotName(user)) {
            return null;
        }
        return (
            <div className={styles.memberControls}>
                {currentTeam !== 'TEAM1' && (
                    <button className={styles.smallButton} onClick={() => assignPlayerToTeam(user, 'TEAM1')}>→ Team 1</button>
                )}
                {currentTeam !== 'TEAM2' && (
                    <button className={styles.smallButton} onClick={() => assignPlayerToTeam(user, 'TEAM2')}>→ Team 2</button>
                )}
                {currentTeam !== 'UNDEFINED' && (
                    <button className={styles.smallButton} onClick={() => assignPlayerToTeam(user, 'UNDEFINED')}>Unassign</button>
                )}
                {user !== myUsername && (
                    <button className={styles.smallButton} onClick={() => makeHost(user)}>Make Host</button>
                )}
            </div>
        );
    };

    return (
        <>
            <div className={styles.wrapper}>
                <div className={styles.panel}>
                    <div className={styles.panelHeader}>
                        <h3 className={styles.panelTitle}>
                            ⚔️ Team Selection
                            {sessionTarget && <span className={styles.sessionBadge}>🏆 Session to {sessionTarget}</span>}
                        </h3>
                        <div className={styles.panelActions}>
                            {inviteLink && (
                                <button
                                    className={`${styles.actionButton} ${styles.inviteButton}`}
                                    onClick={copyInviteLink}
                                    title="Copy a link that drops friends straight into this lobby as guests"
                                >
                                    {copied ? '✅ Link copied!' : '🔗 Copy invite link'}
                                </button>
                            )}
                            {myTeam !== "UNDEFINED" && (
                                <button className={`${styles.actionButton} ${styles.leaveTeamButton}`} onClick={leaveTeam}>
                                    ❌ Leave Team
                                </button>
                            )}
                            {!isHost && (
                                <button
                                    className={`${styles.actionButton} ${amIReady ? styles.readyButtonActive : styles.readyButton}`}
                                    onClick={toggleReady}
                                >
                                    {amIReady ? '✅ Ready' : '⏳ Ready Up'}
                                </button>
                            )}
                            {isHost && (
                                <button className={`${styles.actionButton} ${styles.startGameButton}`} onClick={startGame}>
                                    🚀 Start Game
                                </button>
                            )}
                        </div>
                    </div>

                    {unassignedUsers.length > 0 && (
                        <div className={styles.unassignedBox}>
                            <h4 className={styles.unassignedTitle}>
                                ⏳ Haven't picked team yet . . . ({unassignedUsers.length})
                            </h4>
                            <div className={styles.unassignedList}>
                                {unassignedUsers.map((user) => {
                                    const isMe = user === myUsername;
                                    return (
                                        <div key={user} className={`${styles.unassignedChip} ${isMe ? styles.unassignedChipMe : ''}`}>
                                            <div className={`${styles.avatarDot} ${isMe ? styles.avatarDotMe : ''}`}>
                                                <PlayerAvatar name={user} size={20} />
                                            </div>
                                            <span className={`${styles.chipName} ${isMe ? styles.chipNameMe : ''}`}>
                                                {user} {isMe && '(You)'}
                                            </span>
                                            {renderNameBadges(user)}
                                            {renderHostControls(user, 'UNDEFINED')}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className={styles.teamsGrid}>
                        <div className={`${styles.teamCard} ${styles.teamCardBlue}`}>
                            <div className={styles.teamCardHeader}>
                                <h4 className={`${styles.teamCardTitle} ${styles.teamCardTitleBlue}`}>
                                    🔵 Team 1 ({team1.length}/2)
                                </h4>
                                <button
                                    onClick={() => joinTeam("TEAM1")}
                                    disabled={team1.length >= 2 && myTeam !== "TEAM1"}
                                    className={`${styles.joinButton} ${
                                        team1.length >= 2 && myTeam !== "TEAM1"
                                            ? styles.joinButtonDisabled
                                            : myTeam === "TEAM1" ? styles.joinButtonBlueActive : styles.joinButtonBlue
                                    }`}
                                >
                                    {myTeam === "TEAM1" ? "Joined" : team1.length >= 2 ? "Full" : "Join Team"}
                                </button>
                            </div>

                            <div className={styles.teamMembers}>
                                {team1.length === 0 ? (
                                    <div className={styles.emptyTeam}>No players yet</div>
                                ) : (
                                    team1.map((user) => (
                                        <div key={user} className={`${styles.teamMemberRow} ${styles.teamMemberRowBlue}`}>
                                            <div className={`${styles.teamAvatar} ${styles.teamAvatarBlue}`}>
                                                <PlayerAvatar name={user} size={24} />
                                            </div>
                                            <span className={styles.teamMemberName}>{isBotName(user) ? 'Bot' : user}</span>
                                            {renderNameBadges(user)}
                                            {isHost && isBotName(user) && (
                                                <button className={styles.smallButton} onClick={() => removeBotFromLobby(user)}>✕ Remove</button>
                                            )}
                                            {renderHostControls(user, 'TEAM1')}
                                        </div>
                                    ))
                                )}
                            </div>
                            {isHost && team1.length < 2 && (
                                <div className={styles.addBotRow}>
                                    <select
                                        className={styles.difficultySelect}
                                        value={newBotDifficulty.TEAM1}
                                        onChange={(e) => setNewBotDifficulty({ ...newBotDifficulty, TEAM1: e.target.value as Difficulty })}
                                    >
                                        <option value="EASY">Easy</option>
                                        <option value="MEDIUM">Medium</option>
                                        <option value="HARD">Hard</option>
                                    </select>
                                    <button className={styles.smallButton} onClick={() => addBotToTeam('TEAM1')}>+ Add Bot</button>
                                </div>
                            )}
                        </div>

                        <div className={`${styles.teamCard} ${styles.teamCardRed}`}>
                            <div className={styles.teamCardHeader}>
                                <h4 className={`${styles.teamCardTitle} ${styles.teamCardTitleRed}`}>
                                    🔴 Team 2 ({team2.length}/2)
                                </h4>
                                <button
                                    onClick={() => joinTeam("TEAM2")}
                                    disabled={team2.length >= 2 && myTeam !== "TEAM2"}
                                    className={`${styles.joinButton} ${
                                        team2.length >= 2 && myTeam !== "TEAM2"
                                            ? styles.joinButtonDisabled
                                            : myTeam === "TEAM2" ? styles.joinButtonRedActive : styles.joinButtonRed
                                    }`}
                                >
                                    {myTeam === "TEAM2" ? "Joined" : team2.length >= 2 ? "Full" : "Join Team"}
                                </button>
                            </div>

                            <div className={styles.teamMembers}>
                                {team2.length === 0 ? (
                                    <div className={styles.emptyTeam}>No players yet</div>
                                ) : (
                                    team2.map((user) => (
                                        <div key={user} className={`${styles.teamMemberRow} ${styles.teamMemberRowRed}`}>
                                            <div className={`${styles.teamAvatar} ${styles.teamAvatarRed}`}>
                                                <PlayerAvatar name={user} size={24} />
                                            </div>
                                            <span className={styles.teamMemberName}>{isBotName(user) ? 'Bot' : user}</span>
                                            {renderNameBadges(user)}
                                            {isHost && isBotName(user) && (
                                                <button className={styles.smallButton} onClick={() => removeBotFromLobby(user)}>✕ Remove</button>
                                            )}
                                            {renderHostControls(user, 'TEAM2')}
                                        </div>
                                    ))
                                )}
                            </div>
                            {isHost && team2.length < 2 && (
                                <div className={styles.addBotRow}>
                                    <select
                                        className={styles.difficultySelect}
                                        value={newBotDifficulty.TEAM2}
                                        onChange={(e) => setNewBotDifficulty({ ...newBotDifficulty, TEAM2: e.target.value as Difficulty })}
                                    >
                                        <option value="EASY">Easy</option>
                                        <option value="MEDIUM">Medium</option>
                                        <option value="HARD">Hard</option>
                                    </select>
                                    <button className={styles.smallButton} onClick={() => addBotToTeam('TEAM2')}>+ Add Bot</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {games.length > 0 && (
                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <h3 className={styles.panelTitle}>📊 Game History</h3>
                            <div className={styles.historyCount}>{games.length} games completed</div>
                        </div>

                        <div className={styles.historyTable}>
                            <div className={styles.historyHeaderRow}>
                                <div>Team 1</div>
                                <div>Team 2</div>
                                <div>Score</div>
                                <div>Actions</div>
                            </div>
                            {games.map((game, index) => (
                                <div key={index} className={styles.historyRow}>
                                    <div className={styles.historyTeam}>
                                        <span style={{ color: '#3B82F6', fontWeight: 600 }}>🔵</span>
                                        {game.team1Player1} &amp; {game.team1Player2}
                                    </div>
                                    <div className={styles.historyTeam}>
                                        <span style={{ color: '#EF4444', fontWeight: 600 }}>🔴</span>
                                        {game.team2Player1} &amp; {game.team2Player2}
                                    </div>
                                    <div className={styles.historyScore}>
                                        {game.team1Score} - {game.team2Score}
                                    </div>
                                    <button className={styles.detailsButton} onClick={() => handleDetailsClick(game.gameId)}>
                                        Details
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {selectedId && (<FinishedGameDetailsModal id={selectedId} onClose={() => setSelectedId(null)} />)}
        </>
    );
};

export default LobbyRoom;
